import React, { type ReactNode, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	isSearchResultAtlasGoalPartial,
	isSearchResultAtlasProjectPartial,
	type SearchAtlasPartial,
} from '@atlassian/dynamic-cards-search-client';
import { highlightMatchedText } from '@atlassian/dynamic-cards-search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import SearchMetadataLink from '../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getUrl } from '../../../../../../common/utils/url-utils';
import {
	usePrimaryProduct,
	useSearchQuery,
	useSearchSessionId,
	useSelectedProduct,
} from '../../../../../../controllers/store';

import { messages } from './messages';

const getKey = (partial: SearchAtlasPartial) => {
	if (isSearchResultAtlasProjectPartial(partial)) {
		return partial.project?.key;
	} else if (isSearchResultAtlasGoalPartial(partial)) {
		return partial.goal?.key;
	} else {
		return null;
	}
};

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

const statusDateStyles = xcss({
	font: token('font.body.small'),
	color: 'color.text.subtle',
});

const getStatusAndDate = (partial: SearchAtlasPartial) => {
	let statusLabel: ReactNode = null;
	let statusValue;
	let date: ReactNode = null;

	if (isSearchResultAtlasProjectPartial(partial)) {
		statusLabel = partial.project?.state.label;
		statusValue = partial.project?.state.value;
		date = partial.project?.dueDate.label;
	} else if (isSearchResultAtlasGoalPartial(partial)) {
		const messageKey = partial.goal?.status.localizedLabel.messageId as keyof typeof messages;
		statusLabel = messages[messageKey] ? <FormattedMessage {...messages[messageKey]} /> : null;
		date = partial.goal?.targetDate.label;
	}

	if (!statusLabel || !statusValue) {
		return null;
	}

	return (
		<Box xcss={statusAndDateContainerStyles}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
			<Lozenge style={getStatusLozengeStyle(statusValue)}>{statusLabel}</Lozenge>
			{date && (
				<>
					<Box xcss={statusDateStyles}>
						<FormattedMessage {...messages.for} />
					</Box>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<Lozenge style={defaultStatusLozengeStyle}>{date}</Lozenge>
				</>
			)}
		</Box>
	);
};

const onTrackStatusLozengeStyle: LozengeStyle = {
	backgroundColor: `${token('color.background.accent.green.subtlest')}`,
	color: `${token('color.text.accent.green')}`,
};
const atRiskStatusLozengeStyle: LozengeStyle = {
	backgroundColor: `${token('color.background.accent.yellow.subtlest')}`,
	color: `${token('color.text.accent.orange')}`,
};
const offTrackStatusLozengeStyle: LozengeStyle = {
	backgroundColor: `${token('color.background.accent.red.subtlest')}`,
	color: `${token('color.text.accent.red')}`,
};
const defaultStatusLozengeStyle: LozengeStyle = {
	backgroundColor: `${token('color.background.accent.gray.subtlest')}`,
	color: `${token('color.text.accent.gray')}`,
};

type LozengeStyle = {
	backgroundColor: string;
	color: string;
};

export const getStatusLozengeStyle = (statusType: string): LozengeStyle => {
	switch (statusType) {
		case 'on_track':
			return onTrackStatusLozengeStyle;
		case 'at_risk':
			return atRiskStatusLozengeStyle;
		case 'off_track':
			return offTrackStatusLozengeStyle;
		default:
			return defaultStatusLozengeStyle;
	}
};

const descriptionContainerStyles = xcss({
	overflow: 'hidden',
	maxHeight: '60px',
});

type GoalDescription = {
	type: string;
	content?: GoalDescription[];
	text?: string;
};

const getDescription = (partial: SearchAtlasPartial) => {
	let description = '';

	if (isSearchResultAtlasGoalPartial(partial) && partial.goal?.description) {
		const parsedDescrption: GoalDescription = JSON.parse(partial.goal.description);

		// Recursive function to build the description. Traversal occurs in DFS
		// order. The base case is when the node is a text node, in which case
		// the text is appended to the description.
		const buildDescription = (goalDescription: GoalDescription) => {
			if (goalDescription.content) {
				goalDescription.content.forEach(buildDescription);
			} else if (goalDescription.type === 'text') {
				description += goalDescription.text;
			}
		};

		if (parsedDescrption.content) {
			buildDescription(parsedDescrption);
		}
	}

	return description;
};

interface AtlasSearchResultProps {
	partial: SearchAtlasPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const AtlasSearchResult = ({ partial, position }: AtlasSearchResultProps) => {
	const [searchSessionId] = useSearchSessionId();
	const [selectedProduct] = useSelectedProduct();
	const [searchQuery] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(partial.title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return partial.title;
		}
		return enrichExcerpt(highlightedTitle);
	}, [partial.title, searchQuery]);

	const description = getDescription(partial);

	const enrichedDescription = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(description, searchQuery.split(' '));
		if (!highlightedTitle) {
			return description;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	const url = useMemo(() => {
		return getUrl(partial.url, 'search_id', searchSessionId);
	}, [partial, searchSessionId]);

	const keyLink = useMemo(() => {
		const value = getKey(partial);
		if (!value || !url) {
			return null;
		}
		return <SearchMetadataLink label={value} href={url} />;
	}, [partial, url]);

	return (
		<SearchResult
			{...partial}
			primaryProduct={primaryProduct}
			url={url}
			icon={<ProductIcon product={ProductKeys.Atlas} label="Atlas" />}
			headerMetadata={
				<SearchMetadata
					product="townsquare"
					productSelected={!!selectedProduct}
					parts={[
						['type', <SearchResultType type={partial.type} />],
						['key', keyLink],
						['status', getStatusAndDate(partial)],
					]}
				/>
			}
			title={enrichedTitle}
			description={
				<Box xcss={descriptionContainerStyles} testId="search-page-atlas-result-description">
					{enrichedDescription}
				</Box>
			}
			position={position}
			partial={partial}
		/>
	);
};
